import { useCallback, useRef, useState, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { JobEntryCard } from './JobEntryCard'
import { LoadingMoreTechniciansIndicator } from './LoadingMoreTechniciansIndicator'
import { TechnicianQuickActions } from './TechnicianQuickActions'
import {
  clearAgendaDragPayload,
  getAgendaDragPayload,
  setAgendaDragPreview,
  setAgendaDragPayload,
  updateAgendaDragPreview,
  type AgendaDragPayload,
} from './agenda-dnd'
import { agendaDropTargetClass } from './agenda-drop-feedback'
import {
  sameDay,
  toISODate,
  uniqueJobsWithLane,
  type JobWithLane,
  type TechnicianLane,
} from './grid-helpers'
import { PRELOAD_ROOT_PROPS } from './job-detail-preload'
import {
  DEFAULT_DAY_START_HOUR,
  computeDropTimeRange,
  getJobDurationMinutes,
  minutesToTimeString,
} from './time-geometry'
import { JOB_STATUS, type Job } from '@/types'
import type { AgendaMutationHandlers } from '@/components/jobs/AgendaGrid'

interface WeekViewProps extends Partial<AgendaMutationHandlers> {
  days: Date[]
  lanes: TechnicianLane[]
  jobsByTech: Map<string, Job[]>
  onEntryClick: (job: Job) => void
  onDayClick?: (day: Date) => void
  onLoadMoreTechnicians?: () => void
  hasMoreTechnicians?: boolean
  isLoadingMoreTechnicians?: boolean
  enableTechnicianColumnReorder?: boolean
  enableDragDropAssignment?: boolean
  enableResizeDuration?: boolean
}

const DOW_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const LOAD_MORE_TECHNICIANS_THRESHOLD_PX = 720
const WEEK_ROW_MIN_HEIGHT_PX = 132
const WEEK_CARD_HEIGHT_PX = 80
const WEEK_CARD_GAP_PX = 4
const WEEK_CELL_VERTICAL_PADDING_PX = 8
type DroppableAgendaDragPayload = Exclude<
  AgendaDragPayload,
  { type: 'technician-column' }
>

function laneJobsForDay(
  jobsWithLane: JobWithLane[],
  lane: TechnicianLane,
  day: Date
) {
  return jobsWithLane
    .filter(
      ({ job, lane: jobLane }) =>
        jobLane.technicianId === lane.technicianId &&
        Boolean(job.scheduled_date && sameDay(job.scheduled_date, day))
    )
    .sort((a, b) =>
      (a.job.start_time ?? '').localeCompare(b.job.start_time ?? '')
    )
}

function assignedTechnicianIdsForDrag(job: Job, laneTechnicianId: string) {
  const assignedIds = (job.assigned_technicians ?? [])
    .map(technician => technician.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)

  if (assignedIds.length === 0) return [laneTechnicianId]
  return Array.from(new Set(assignedIds))
}

function nextTechnicianIdsForDrop(
  payload: Extract<AgendaDragPayload, { type: 'scheduled-job' }>,
  targetTechnicianId: string
) {
  const currentIds =
    payload.technicianIds.length > 0
      ? payload.technicianIds
      : [payload.fromTechnicianId]

  if (payload.fromTechnicianId === targetTechnicianId) {
    return Array.from(new Set(currentIds))
  }

  const nextIds = currentIds.map(id =>
    id === payload.fromTechnicianId ? targetTechnicianId : id
  )
  if (!nextIds.includes(targetTechnicianId)) nextIds.push(targetTechnicianId)

  return Array.from(new Set(nextIds))
}

function timeRangeForDropPayload(payload: DroppableAgendaDragPayload) {
  if (payload.startTime && payload.endTime) {
    return { start_time: payload.startTime, end_time: payload.endTime }
  }

  const { start, end } = computeDropTimeRange({
    pointerMinutes: DEFAULT_DAY_START_HOUR * 60,
    dayStartHour: DEFAULT_DAY_START_HOUR,
    durationMinutes: payload.durationMinutes,
  })

  return {
    start_time: minutesToTimeString(start),
    end_time: minutesToTimeString(end),
  }
}

export function WeekView({
  days,
  lanes,
  jobsByTech,
  onEntryClick,
  onDayClick,
  onScheduleJob,
  isMutatingJobId,
  onLoadMoreTechnicians,
  hasMoreTechnicians = false,
  isLoadingMoreTechnicians = false,
  enableDragDropAssignment = true,
}: WeekViewProps) {
  const { t } = useTranslation('jobs')
  const todayIso = toISODate(new Date())
  const jobsWithLane = uniqueJobsWithLane(lanes, jobsByTech)
  const [dropCellId, setDropCellId] = useState<string | null>(null)
  const [draggingJobId, setDraggingJobId] = useState<string | null>(null)
  const lastScrollTopRef = useRef(0)
  const canDragDropAssignments =
    enableDragDropAssignment && Boolean(onScheduleJob)
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (
        !hasMoreTechnicians ||
        isLoadingMoreTechnicians ||
        !onLoadMoreTechnicians
      ) {
        return
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
      const lastScrollTop = lastScrollTopRef.current
      lastScrollTopRef.current = scrollTop

      if (scrollHeight <= clientHeight || scrollTop <= lastScrollTop) return

      if (
        scrollHeight - scrollTop - clientHeight <=
        LOAD_MORE_TECHNICIANS_THRESHOLD_PX
      ) {
        onLoadMoreTechnicians()
      }
    },
    [hasMoreTechnicians, isLoadingMoreTechnicians, onLoadMoreTechnicians]
  )

  const getSchedulePayload = (dataTransfer: DataTransfer) => {
    if (!canDragDropAssignments) return null
    const payload = getAgendaDragPayload(dataTransfer)
    return payload && payload.type !== 'technician-column' ? payload : null
  }

  return (
    <div
      className='h-[calc(100vh-250px)] min-h-[560px] overflow-auto bg-white xl:h-auto xl:min-h-0 xl:flex-1'
      {...PRELOAD_ROOT_PROPS}
      onScroll={handleScroll}
    >
      <div
        className='flex w-full flex-col xl:h-full'
        style={{ minWidth: 200 + days.length * 260 }}
      >
        <div className='sticky top-0 z-30 flex shrink-0 bg-white'>
          <div className='sticky left-0 z-40 h-16 w-[200px] shrink-0 border-b border-r border-[#e7e9ef] bg-white shadow-[1px_0_4px_rgba(26,27,30,0.08)]' />
          {days.map((day, index) => {
            const dayIso = toISODate(day)
            const interventionCount = jobsWithLane.filter(({ job }) =>
              Boolean(job.scheduled_date && sameDay(job.scheduled_date, day))
            ).length
            return (
              <button
                type='button'
                key={dayIso}
                onClick={() => onDayClick?.(new Date(day))}
                className={`flex h-16 min-w-[260px] flex-1 flex-col justify-center gap-1 border-b border-r border-[#e7e9ef] bg-white px-3 text-left outline-none transition-colors hover:bg-[#fbfbfc] focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-[-2px] ${
                  dayIso === todayIso ? 'shadow-[inset_0_-2px_0_#ff5a1f]' : ''
                }`}
              >
                <span
                  className={`font-sans text-sm font-medium leading-none ${
                    dayIso === todayIso ? 'text-[#ff5a1f]' : 'text-[#171a20]'
                  }`}
                >
                  {t(`agenda.weekdays.${DOW_KEYS[index % 7]}`)} {day.getDate()}
                </span>
                <span className='font-sans text-xs font-normal leading-4 text-[#3d434e]'>
                  {t('agenda.interventions', { count: interventionCount })}
                </span>
              </button>
            )
          })}
        </div>

        {lanes.map(lane => {
          const laneTotal = jobsWithLane.filter(
            entry => entry.lane.technicianId === lane.technicianId
          ).length
          const maxEntriesInDay = Math.max(
            1,
            ...days.map(day => laneJobsForDay(jobsWithLane, lane, day).length)
          )
          const rowHeight = Math.max(
            WEEK_ROW_MIN_HEIGHT_PX,
            WEEK_CELL_VERTICAL_PADDING_PX +
              maxEntriesInDay * WEEK_CARD_HEIGHT_PX +
              (maxEntriesInDay - 1) * WEEK_CARD_GAP_PX
          )
          return (
            <div
              key={lane.technicianId}
              className='flex min-h-[132px]'
              style={{ minHeight: rowHeight }}
            >
              <TechnicianRowHeader
                lane={lane}
                total={laneTotal}
                minHeight={rowHeight}
              />
              {days.map(day => {
                const entries = laneJobsForDay(jobsWithLane, lane, day)
                const cellId = `${lane.technicianId}-${toISODate(day)}`
                const isDropActive = dropCellId === cellId
                const isDropBlocked = isDropActive && Boolean(isMutatingJobId)
                return (
                  <div
                    key={cellId}
                    onDragEnter={
                      canDragDropAssignments
                        ? event => {
                            if (getSchedulePayload(event.dataTransfer)) {
                              setDropCellId(cellId)
                            }
                          }
                        : undefined
                    }
                    onDragOver={
                      canDragDropAssignments
                        ? event => {
                            const payload = getSchedulePayload(
                              event.dataTransfer
                            )
                            if (!payload) return
                            event.preventDefault()
                            event.dataTransfer.dropEffect = isDropBlocked
                              ? 'none'
                              : 'move'
                            updateAgendaDragPreview(
                              event.clientX,
                              event.clientY,
                              isDropBlocked ? null : lane.name
                            )
                            setDropCellId(cellId)
                          }
                        : undefined
                    }
                    onDragLeave={
                      canDragDropAssignments
                        ? event => {
                            if (
                              !event.currentTarget.contains(
                                event.relatedTarget as Node
                              )
                            ) {
                              setDropCellId(current =>
                                current === cellId ? null : current
                              )
                            }
                          }
                        : undefined
                    }
                    onDrop={
                      canDragDropAssignments
                        ? event => {
                            const payload = getSchedulePayload(
                              event.dataTransfer
                            )
                            if (!payload) return
                            event.preventDefault()
                            setDropCellId(null)
                            setDraggingJobId(null)
                            if (isDropBlocked || !onScheduleJob) {
                              clearAgendaDragPayload()
                              return
                            }

                            const timeRange = timeRangeForDropPayload(payload)
                            onScheduleJob(payload.jobId, {
                              scheduled_date: toISODate(day),
                              ...timeRange,
                              technician_ids:
                                payload.type === 'scheduled-job'
                                  ? nextTechnicianIdsForDrop(
                                      payload,
                                      lane.technicianId
                                    )
                                  : [lane.technicianId],
                            })
                            clearAgendaDragPayload()
                          }
                        : undefined
                    }
                    onClick={event => {
                      const target = event.target as Element | null
                      if (target?.closest('[data-agenda-job-card]')) return
                      onDayClick?.(new Date(day))
                    }}
                    className={`min-h-[132px] min-w-[260px] flex-1 border-b border-r border-[#e7e9ef] bg-[#fbfbfc] p-1 ${agendaDropTargetClass(isDropActive, isDropBlocked)}`}
                    style={{ minHeight: rowHeight }}
                  >
                    <div className='flex flex-col gap-1'>
                      {entries.map(({ job }) => {
                        const canDragScheduledJob =
                          job.status === JOB_STATUS.planned ||
                          job.status === JOB_STATUS.assigned
                        const isMutating = job.id === isMutatingJobId
                        return (
                          <JobEntryCard
                            key={job.id}
                            job={job}
                            color={lane.color}
                            tint={lane.tint}
                            onClick={() => onEntryClick(job)}
                            variant='week'
                            draggable={
                              canDragDropAssignments &&
                              canDragScheduledJob &&
                              !isMutating
                            }
                            onDragStart={
                              canDragDropAssignments
                                ? event => {
                                    setDraggingJobId(job.id)
                                    setAgendaDragPayload(event.dataTransfer, {
                                      type: 'scheduled-job',
                                      jobId: job.id,
                                      fromTechnicianId: lane.technicianId,
                                      technicianIds:
                                        assignedTechnicianIdsForDrag(
                                          job,
                                          lane.technicianId
                                        ),
                                      durationMinutes:
                                        getJobDurationMinutes(job),
                                      status: job.status,
                                      grabOffsetY:
                                        event.clientY -
                                        event.currentTarget.getBoundingClientRect()
                                          .top,
                                      startTime: job.start_time,
                                      endTime: job.end_time,
                                    })
                                    setAgendaDragPreview(
                                      event.dataTransfer,
                                      event.currentTarget,
                                      event.clientX,
                                      event.clientY
                                    )
                                    event.dataTransfer.effectAllowed = 'move'
                                  }
                                : undefined
                            }
                            onDragEnd={
                              canDragDropAssignments
                                ? () => {
                                    setDropCellId(null)
                                    setDraggingJobId(null)
                                    clearAgendaDragPayload()
                                  }
                                : undefined
                            }
                            ariaDisabled={!canDragScheduledJob}
                            isMutating={isMutating}
                            className={
                              draggingJobId === job.id ? 'opacity-0' : undefined
                            }
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}

        {isLoadingMoreTechnicians && (
          <LoadingMoreTechniciansIndicator
            loading={isLoadingMoreTechnicians}
            className='sticky left-0 z-20 h-14 w-full border-t border-[#e7e9ef] bg-white/95'
          />
        )}
      </div>
    </div>
  )
}

function TechnicianRowHeader({
  lane,
  total,
  minHeight,
}: {
  lane: TechnicianLane
  total: number
  minHeight: number
}) {
  const { t } = useTranslation('jobs')

  return (
    <div
      className='sticky left-0 z-20 flex min-h-[132px] w-[200px] shrink-0 flex-col gap-2 border-b border-r border-[#e7e9ef] bg-white p-3 pl-4 shadow-[1px_0_4px_rgba(26,27,30,0.08)]'
      style={{ minHeight }}
    >
      <span
        aria-hidden='true'
        className='pointer-events-none absolute bottom-0 left-0 top-0 w-1'
        style={{ backgroundColor: lane.color }}
      />
      <div className='flex min-w-0 items-center gap-2'>
        <span
          className='h-3 w-3 shrink-0 rounded-full'
          style={{ backgroundColor: lane.color }}
        />
        <span className='min-w-0 flex-1 truncate font-sans text-base font-medium text-[#171a20]'>
          {lane.name}
        </span>
        <TechnicianQuickActions lane={lane} />
      </div>
      <span className='text-left font-sans text-xs font-normal leading-4 text-[#69758c]'>
        {t('agenda.totalShort', { count: total })}
      </span>
    </div>
  )
}
